import { Loader2 } from 'lucide-react';
import type { Severity } from '@kirin/api-client/contracts/shared/severity';
import { SeverityBadge } from '@/components/policylist/components/SeverityBadge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  bulkSeverityModalCopy,
  bulkSeverityResetModalCopy,
  type BulkSeverityModalCopy,
} from './bulkActionCopy';

/**
 * Override to one level, confirmed against the server-resolved
 * `eligibility.totalSelected`; or reset to default, confirmed against the
 * preview's overridden / not-overridden split (KIR-5192).
 */
export type BulkSeverityDialogTarget =
  | { severity: Severity; totalSelected: number }
  | { severity: 'reset'; eligibleCount: number; alreadyCount: number };

export type BulkSeverityConfirmDialogProps = BulkSeverityDialogTarget & {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isPending: boolean;
  onConfirm: () => void;
};

/**
 * Confirmation modal for the bulk "Override Severity" menu (KIR-5192): a
 * level, or "Reset to Default". Kept separate from
 * {@link BulkActionConfirmDialog}: severity has no duration and no destructive
 * styling, so folding it in would add a branch to every part of that dialog.
 * Reset shows no "New severity" badge, because each alert returns to its own
 * level.
 */
export function BulkSeverityConfirmDialog(
  props: BulkSeverityConfirmDialogProps
): React.ReactElement {
  const { open, onOpenChange, isPending, onConfirm } = props;
  let severity: Severity | null;
  let copy: BulkSeverityModalCopy;
  if (props.severity === 'reset') {
    severity = null;
    copy = bulkSeverityResetModalCopy({
      eligibleCount: props.eligibleCount,
      alreadyCount: props.alreadyCount,
    });
  } else {
    severity = props.severity;
    copy = bulkSeverityModalCopy(props.severity, props.totalSelected);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{copy.title}</DialogTitle>
          <DialogDescription>{copy.body}</DialogDescription>
        </DialogHeader>

        {(severity !== null || copy.countLine) && (
          <div className="flex flex-col gap-3 py-1 text-sm">
            {severity !== null && (
              <div className="flex items-center gap-2">
                <span className="font-medium">New severity</span>
                <SeverityBadge riskLevel={severity} showIcon />
              </div>
            )}
            {copy.countLine && (
              <p className="text-muted-foreground">{copy.countLine}</p>
            )}
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button type="button" onClick={onConfirm} disabled={isPending}>
            {isPending && <Loader2 className="size-4 animate-spin" />}
            {copy.confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

BulkSeverityConfirmDialog.displayName = 'BulkSeverityConfirmDialog';
